<script>
    // Packages
    import {ref, reactive} from 'vue';
    import { useForm, usePage } from '@inertiajs/vue3';
    import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue';
    
    // Components
    import SlideOutErrors from "@/Components/SlideOutErrors.vue";

    // Icons
    import {XIcon} from '@heroicons/vue/outline';
    import {LinkIcon, PlusSmIcon, QuestionMarkCircleIcon, ExclamationCircleIcon} from '@heroicons/vue/solid';


    export default {
        components: {
            XIcon,
            Dialog,
            LinkIcon,
            PlusSmIcon,
            DialogTitle,
            DialogOverlay,
            SlideOutErrors,
            TransitionRoot,
            TransitionChild,
            ExclamationCircleIcon,
            QuestionMarkCircleIcon
        },
        setup(props, context) {

            const users = usePage().props.users;

            const show = ref(false)

            const showErrors = ref(false)

            const updating = false;

            const form = useForm({
                id: null,
                name: null,
                card_number: null,
                expiry_month: null,
                expiry_year: null,
                is_group_card: null,
                user_id: null
            })

            function submit() {
                if(this.updating) {
                    this.form.post(route('admin.settings.credit-cards.card.update', this.form.id), {
                        onSuccess: page => {
                            this.show = false;
                            this.showErrors = false;
                            this.form.reset();
                        },
                        onError: errors => {
                            this.showErrors = true;
                        }
                    });
                } else {
                    this.form.post(route('admin.settings.credit-cards.create'), {
                        onSuccess: page => {
                            this.show = false;
                            this.showErrors = false;
                            this.form.reset();
                        },
                        onError: errors => {
                            this.showErrors = true;
                        }
                    });
                }
            }

            function close() {
                this.form.reset();
                this.form.clearErrors();
                this.show = false;
            }

            function open() {
                this.form.clearErrors();
                this.show = true;
            }

            return {
                show,
                open,
                form,
                close,
                users,
                submit,
                updating,
                showErrors
            }
        },
    }
</script>
<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75"/>
                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transform transition ease-in-out duration-500 sm:duration-700"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-lg">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl" @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4 bg-dark-pitch-purple sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white font-bold" v-if="form.id">
                                                Update Credit Card `{{ form.name }}`
                                            </DialogTitle>
                                            <DialogTitle class="text-lg text-white font-bold" v-else>
                                                Create Credit Card
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button" class="bg-dark-pitch-purple rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white" @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-white">
                                                <span v-if="form.id">
                                                    Get started by filling in the information below to create a new credit card.
                                                </span>
                                                <span v-else>
                                                    Get started by filling in the information below to update the credit card.
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors === true"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="space-y-6 pt-6 pb-5">
                                                <div>
                                                    <label for="name" class="block text-sm text-gray-900 required">
                                                        Name
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="name" v-model="form.name"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{'border-red-500': form.errors.name}"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="card_number" class="block text-sm text-gray-900 required">
                                                        Number <i>(Last 4 Digits)</i>
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="card_number" v-model="form.card_number" maxlength="4" pattern="\d*"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{'border-red-500': form.errors.card_number}"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="expiry_month" class="block text-sm text-gray-900 required">
                                                        Expiry Month
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="expiry_month" v-model="form.expiry_month" maxlength="2" pattern="\d*"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{'border-red-500': form.errors.expiry_month}"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="expiry_year" class="block text-sm text-gray-900 required">
                                                        Expiry Year
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="expiry_year" v-model="form.expiry_year" maxlength="4" pattern="\d*"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{'border-red-500': form.errors.expiry_year}"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="is_group_card" class="block text-sm text-gray-900 required">
                                                        Group Card
                                                    </label>
                                                    <div class="mt-1">
                                                        <v-select id="is_group_card" v-model="form.is_group_card"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                            :class="{'border-red-500': form.errors.is_group_card}"
                                                            :options="[{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }]"
                                                            label="label" 
                                                            :reduce="value => value.value"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="user_id" class="block text-sm font-medium text-gray-700">
                                                        User
                                                    </label>
                                                    <v-select class="mt-1" :class="{'border-red-500': form.errors.user_id}" :options="users" label="name" :reduce="(users) => users.id" v-model="form.user_id"/>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button" class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" @click="close()">
                                        Close
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="submit" class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-dark-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200">
                                        <span v-if="updating === true">Update</span>
                                        <span v-else>Save</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>